import { useCallback, useEffect, useState } from 'react';
import { Printer } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { UniversalWindow } from '@/components/ui/UniversalWindow';
import { getSDK } from '@/store/sdkStore';
import { useToastStore } from '@/store/toastStore';
import { friendlyError, logErrorToJournal } from '@/lib/errors';
import { downloadBytes, isPreviewableMime, printFormatFor, printFormatLabel, base64ToBlob } from '@/lib/printForm';

// ============================================================================
// PrintListDialog — ПЕЧАТЬ СПИСКА (расширение механизма печати шага 6).
// Печатные формы списка — те же макеты объекта (is_print, parent_type='object'),
// но рендер идёт ПО СТРОКАМ: клиент передаёт текущие строки списка в
// params.rows (JSON), сервер повторяет «область данных» макета для каждой
// строки (в ячейках — {{column_name}} / {{column_name_presentation}}).
// SSoT механики печати — сервер (internal/layout); клиент лишь выбирает форму.
// ============================================================================

interface Props {
  objectType: string; // catalog | document | task | order
  objectName: string;
  rows: Record<string, unknown>[];
  onClose: () => void;
}

interface PrintLayout { id: string; name: string; synonym: string; layoutType: string; }
interface RenderedPrint { layoutName: string; fileName: string; mimeType: string; previewUrl: string; dataB64: string; }

export function PrintListDialog({ objectType, objectName, rows, onClose }: Props) {
  const [forms, setForms] = useState<PrintLayout[] | null>(null);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState('');
  const [preview, setPreview] = useState<RenderedPrint | null>(null);
  const showSuccess = useToastStore((s) => s.showSuccess);

  useEffect(() => {
    let cancelled = false;
    getSDK().metadata.listLayouts({ parentType: 'object', parentCode: objectName, isPrint: true })
      .then((r: { items?: unknown[] }) => {
        if (cancelled) return;
        setForms(((r.items || []) as Array<Record<string, unknown>>).map((l) => ({
          id: String(l.id), name: String(l.name || ''), synonym: String(l.synonym || ''),
          layoutType: String(l.layoutType || ''),
        })));
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        logErrorToJournal(e, 'PrintListDialog.loadForms');
        setError(friendlyError(e));
        setForms([]);
      });
    return () => { cancelled = true; };
  }, [objectName]);

  const renderForm = useCallback(async (form: PrintLayout) => {
    let format = '';
    try {
      format = printFormatFor(form.layoutType);
    } catch (e) {
      setError(friendlyError(e));
      return;
    }
    setBusyId(form.id);
    setError('');
    try {
      const r: Record<string, unknown> = await getSDK().metadata.renderLayout({
        layoutId: form.id, params: { rows: JSON.stringify(rows) }, format,
      });
      const dataB64 = String(r.data || '');
      const mimeType = String(r.mimeType || 'application/octet-stream');
      const fileName = String(r.fileName || `${form.synonym || form.name}.${format}`);
      if (isPreviewableMime(mimeType)) {
        setPreview({ layoutName: form.synonym || form.name, fileName, mimeType, dataB64, previewUrl: URL.createObjectURL(base64ToBlob(dataB64, mimeType)) });
      } else {
        downloadBytes(fileName, mimeType, dataB64);
        showSuccess(`Список напечатан: ${fileName}`);
        onClose();
      }
    } catch (e) {
      logErrorToJournal(e, 'PrintListDialog.render');
      setError(friendlyError(e));
    } finally {
      setBusyId('');
    }
  }, [rows, onClose, showSuccess]);

  const closePreview = () => {
    if (preview) URL.revokeObjectURL(preview.previewUrl);
    setPreview(null);
  };

  return (
    <>
      <UniversalWindow open onClose={onClose} title="Печать списка" blocking={false} width="max-w-md">
        <div className="p-4 space-y-2">
          <div className="text-xs text-muted-foreground">
            Строк к печати: <b>{rows.length}</b>. Выберите печатную форму:
          </div>
          {forms === null && <div className="text-xs text-muted-foreground">Загрузка форм…</div>}
          {forms !== null && forms.length === 0 && (
            <div className="text-xs text-destructive">У объекта нет печатных форм (макет → «Печатная форма»)</div>
          )}
          {forms !== null && forms.map((f) => {
            let formatLabel = f.layoutType;
            try { formatLabel = printFormatLabel(printFormatFor(f.layoutType)); } catch { /* неизвестный тип */ }
            return (
              <button key={f.id} className="w-full flex items-center justify-between gap-2 rounded border px-3 py-2 text-xs hover:bg-accent/40"
                disabled={!!busyId} onClick={() => void renderForm(f)}>
                <span className="flex items-center gap-2">
                  <Printer className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="font-medium">{f.synonym || f.name}</span>
                </span>
                <span className="text-muted-foreground">{formatLabel}</span>
              </button>
            );
          })}
          {error && <div className="text-xs text-destructive break-words">{error}</div>}
        </div>
      </UniversalWindow>

      {preview && (
        <UniversalWindow open onClose={closePreview} title={`Печать: ${preview.layoutName}`} width="max-w-4xl">
          <div className="p-3 flex flex-col gap-2">
            <iframe src={preview.previewUrl} title={preview.fileName} className="w-full h-[70vh] rounded border bg-white" />
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={closePreview}>Закрыть</Button>
              <Button size="sm" onClick={() => downloadBytes(preview.fileName, preview.mimeType, preview.dataB64)}>Скачать</Button>
            </div>
          </div>
        </UniversalWindow>
      )}
    </>
  );
}
